/**
 * questionari/tipi — le forme del motore questionari di piattaforma (CRM del super utente).
 * ============================================================================
 *
 * UNA sola definizione, usata da quattro posti: il server (`functions/`, copiata in `_shared-pkg`
 * dal suo `sync:shared`), la pagina pubblica sul sito (`marketing-site/`, alias `@shared`), la
 * console (`console-app/`, alias `@shared`) e le prove. Prima i tipi erano scritti due volte
 * (server e console) e la pagina pubblica ne aveva una terza copia implicita.
 *
 * REGOLA: il motore è GENERICO. Domande, opzioni, testi delle schermate e dell'email di conferma
 * sono DATI del questionario (`platform_questionnaires/{id}`); nel codice non c'è niente che sappia
 * cosa sia una lista d'attesa o un settore. La lista d'attesa di YumaOS (18/10/2026) è il primo uso
 * che ha chiesto questi campi, non un caso speciale.
 *
 * Identificatori ≠ etichette (regola cardine #1): ogni opzione ha un `id` ASCII che finisce nelle
 * risposte e nelle statistiche, e un'`etichetta` che si legge e si può riscrivere senza perdere i
 * conti. Le opzioni dei questionari nati prima (semplici stringhe) valgono come id di se stesse.
 *
 * File puro: nessun import. Il server lo compila col target ES2017 (niente `flatMap`,
 * `Object.fromEntries`, `.at()`).
 *
 * Doc canonico: docs/01-ARCHITECTURE/PLATFORM-CRM-QUESTIONARI-MODEL.md
 */

// ─────────────────────────────────────────────────────────────────────────────
// Raccolte (scritte solo dal server, lette dal super utente)
// ─────────────────────────────────────────────────────────────────────────────

export const RACCOLTA_LEAD_PIATTAFORMA = 'platform_leads';
export const RACCOLTA_QUESTIONARI = 'platform_questionnaires';
export const RACCOLTA_LINK_QUESTIONARI = 'platform_questionnaire_links';
export const RACCOLTA_RISPOSTE_QUESTIONARI = 'platform_questionnaire_responses';
/** Una visita della pagina pubblica: serve al «chi apre e chi finisce» e come biglietto anti-programmi. */
export const RACCOLTA_SESSIONI_QUESTIONARI = 'platform_questionnaire_sessioni';
/** I biglietti dei link nell'email di conferma (conferma del consenso, uscita), per impronta. */
export const RACCOLTA_AZIONI_QUESTIONARI = 'platform_questionnaire_azioni';
/** Contatori anti-abuso per indirizzo di rete (impronta, mai in chiaro), con `expires_at` per il TTL. */
export const RACCOLTA_LIMITI_QUESTIONARI = 'platform_questionnaire_limiti';
/** Un'email di conferma al giorno per indirizzo e questionario: il documento è il lucchetto. */
export const RACCOLTA_INVII_QUESTIONARI = 'platform_questionnaire_invii';

// ─────────────────────────────────────────────────────────────────────────────
// Domande
// ─────────────────────────────────────────────────────────────────────────────

export const TIPI_DOMANDA = [
  'testo_breve',
  'testo_lungo',
  'scelta_singola',
  'scelta_multipla',
  'scala_1_10',
  'si_no',
  'numero',
] as const;
export type TipoDomanda = (typeof TIPI_DOMANDA)[number];

/** I tipi con un elenco di opzioni. */
export const TIPI_CON_OPZIONI: readonly TipoDomanda[] = ['scelta_singola', 'scelta_multipla'];

/** Un'opzione di una domanda a scelta. */
export interface OpzioneDomanda {
  /** Codice fisso (ASCII, snake_case): va nelle risposte e nelle statistiche. Mai tradotto. */
  id: string;
  /** Il testo che si legge: si può riscrivere senza perdere i conti. */
  etichetta: string;
  /** Solo nelle scelte multiple: sceglierla toglie le altre, e sceglierne un'altra toglie lei. */
  esclusiva?: boolean;
}

/**
 * Il campo di testo che compare SOTTO le opzioni quando se ne sceglie una di `mostra_se`
 * («Altro» → «Quali altri prodotti o servizi?»; «Un gestionale» o «Un CRM» → «Come si chiama?»).
 * Uno per domanda: due opzioni possono aprire lo stesso campo.
 */
export interface CampoDettaglio {
  etichetta: string;
  segnaposto?: string;
  obbligatorio?: boolean;
  /** Gli id delle opzioni che lo fanno comparire. */
  mostra_se: string[];
}

/** Un tasto rapido della domanda numerica («Solo io» = 1, «5», «10»…). */
export interface ScorciatoiaNumero {
  valore: number;
  /** Il testo del tasto; senza, il numero stesso. */
  etichetta?: string;
}

/** Salto condizionale: se la risposta corrisponde, il flusso salta in avanti a un'altra domanda. */
export interface SaltoCondizionale {
  /** Per le scelte è l'id dell'opzione; per si_no «si»/«no»; per gli altri il valore in testo. */
  se_risposta: string;
  vai_a_domanda_id: string;
}

/** Una domanda così com'è salvata nel questionario (le opzioni possono essere ancora stringhe). */
export interface DomandaSalvata {
  id: string;
  tipo: TipoDomanda;
  testo: string;
  obbligatoria: boolean;
  /** Il nome corto per statistiche, colonne dell'esportazione e riepilogo nell'email («Strumenti scelti»). */
  nome_breve?: string;
  /** La riga di aiuto sotto la domanda («Puoi scegliere più risposte»). */
  aiuto?: string;
  /** Il testo d'esempio dentro il campo (testi e numeri). */
  segnaposto?: string;
  opzioni?: Array<string | OpzioneDomanda>;
  /** Solo scelta_multipla: quante opzioni al massimo. */
  massimo_scelte?: number;
  campo_dettaglio?: CampoDettaglio;
  /** Solo numero: limiti (interi). Senza minimo vale 0. */
  minimo?: number;
  massimo?: number;
  scorciatoie?: ScorciatoiaNumero[];
  salta_a?: SaltoCondizionale;
}

/** La stessa domanda dopo `normalizzaDomanda`: opzioni sempre come oggetti. */
export interface Domanda extends Omit<DomandaSalvata, 'opzioni'> {
  opzioni?: OpzioneDomanda[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Schermate (testi come dati)
// ─────────────────────────────────────────────────────────────────────────────

/** La prima schermata. Senza, la pagina usa titolo e descrizione del questionario. */
export interface SchermataApertura {
  titolo: string;
  testo?: string;
  /** La piccola etichetta sotto il testo («7 domande, poi i tuoi dati»). */
  etichetta?: string;
  /** Il testo del bottone; senza, la pagina mette il suo («Inizia»). */
  bottone?: string;
}

/**
 * I campi possibili dell'ultima schermata. È un insieme CHIUSO perché ognuno ha un posto preciso
 * nella scheda del lead (`platform_leads`): `azienda` → ragione_sociale, `nome` → referente_nome…
 */
export const CAMPI_CONTATTO = [
  'email',
  'nome',
  'ruolo',
  'azienda',
  'telefono',
  'partita_iva',
  'citta',
  'provincia',
] as const;
export type CampoContattoId = (typeof CAMPI_CONTATTO)[number];

/** I campi che riconoscono un'azienda già vista: almeno uno dei due dev'essere obbligatorio. */
export const CAMPI_CHIAVE_LEAD: readonly CampoContattoId[] = ['partita_iva', 'email'];

export interface CampoContatto {
  id: CampoContattoId;
  etichetta: string;
  obbligatorio: boolean;
  segnaposto?: string;
  /** La riga sotto il campo (perché lo chiediamo). */
  nota?: string;
}

export const CANALI_AGGIORNAMENTI = ['email', 'whatsapp'] as const;
export type CanaleAggiornamenti = (typeof CANALI_AGGIORNAMENTI)[number];

/**
 * La casella del consenso agli aggiornamenti: SEPARATA dall'iscrizione e mai già spuntata
 * (Garante, caso Verisure). Il testo esatto e la sua versione si salvano con la risposta: sono la
 * prova di che cosa la persona ha accettato.
 */
export interface ConsensoQuestionario {
  testo: string;
  /** Cambia ogni volta che cambia il testo (es. «2026-10-18»). */
  versione: string;
  canali: CanaleAggiornamenti[];
  /** Il collegamento all'informativa, dopo il testo. */
  etichetta_informativa?: string;
  url_informativa?: string;
}

export interface SchermataContatti {
  /** Il testo in alto al posto di «N di M» («Ultimo passo»). */
  indicatore?: string;
  domanda: string;
  aiuto?: string;
  campi: CampoContatto[];
  consenso?: ConsensoQuestionario;
  /** Il bottone di invio («Iscriviti»). */
  bottone?: string;
}

/** Il collegamento in fondo alla conferma («… sulla nostra pagina LinkedIn»). */
export interface CollegamentoConferma {
  prima: string;
  etichetta: string;
  url: string;
}

export interface SchermataConferma {
  /** La scritta grande in alto («Da marzo 2027»). */
  in_grande?: string;
  titolo: string;
  testo?: string;
  /**
   * Il paragrafo in più per chi ha spuntato il consenso: gli aggiornamenti partono solo dopo il
   * clic nell'email, quindi va detto di aprirla.
   */
  testo_se_consenso?: string;
  collegamento?: CollegamentoConferma;
}

/**
 * L'email che parte dopo l'invio, a chi ha lasciato l'indirizzo. I testi possono contenere
 * `{nome}` e `{azienda}`. Il bottone di conferma degli aggiornamenti compare solo a chi ha spuntato
 * il consenso; il collegamento per uscire c'è sempre.
 */
export interface EmailConferma {
  attiva: boolean;
  oggetto: string;
  /** La riga che i programmi di posta mostrano accanto all'oggetto. */
  anteprima?: string;
  in_grande?: string;
  titolo: string;
  testo: string;
  /** Il titolo del riepilogo delle risposte («Cosa ci hai detto»); senza, niente riepilogo. */
  titolo_riepilogo?: string;
  /** Il paragrafo sopra il bottone di conferma degli aggiornamenti. */
  testo_consenso?: string;
  bottone_consenso?: string;
  /** Il perché la si riceve, in fondo. */
  perche: string;
  /** Il testo del collegamento per uscire. */
  etichetta_uscita?: string;
  /**
   * Dove vanno le risposte a questa email (un indirizzo letto da una persona). Senza, chi risponde
   * scrive al mittente automatico della piattaforma, che nessuno legge.
   */
  rispondi_a?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Documenti
// ─────────────────────────────────────────────────────────────────────────────

/** platform_questionnaires/{id} (i campi del motore; date e autore li aggiunge chi scrive). */
export interface QuestionarioSalvato {
  /** Il nome del questionario: si vede nella console, nella pagina pubblica e nell'email. */
  titolo: string;
  /**
   * Testo PUBBLICO: è l'introduzione della pagina quando manca `apertura` (questionari nati prima).
   * Mai note interne qui: arrivano a chi compila.
   */
  descrizione?: string | null;
  attivo: boolean;
  /**
   * Il modo VECCHIO di chiedere i dati (ragione sociale + partita IVA + email): vale solo se
   * `contatti` manca. I questionari nuovi dichiarano `contatti`.
   */
  richiede_anagrafica?: boolean;
  domande: DomandaSalvata[];
  apertura?: SchermataApertura | null;
  contatti?: SchermataContatti | null;
  conferma?: SchermataConferma | null;
  email_conferma?: EmailConferma | null;
  /**
   * La lingua in cui sono scritti i testi di primo livello (ISO a 2 lettere; predefinita 'it').
   * IMMUTABILE: è una proprietà del testo (regola di `testoMultilingua`, valida per tutto il
   * gestionale). Le altre lingue stanno in `traduzioni`.
   */
  lingua_sorgente?: string;
  /**
   * Le ALTRE lingue: mappa parziale, il valore è SEMPRE un oggetto con la stessa forma dei testi,
   * indicizzato per CODICE (domande, opzioni, campi) e mai per posizione. Una lingua assente è la
   * normalità: si legge il testo sorgente, campo per campo (`questionarioInLingua`).
   */
  traduzioni?: { [lingua: string]: TraduzioneQuestionario | undefined } | null;
  /**
   * Che voce lascia una risposta nel registro dell'azienda (`registro.ts`): `iscrizione` per una lista
   * d'attesa, `questionario_compilato` (predefinito) per tutti gli altri. È un DATO del questionario: il
   * codice non sa quale questionario sia una lista d'attesa.
   */
  tipo_nel_registro?: 'iscrizione' | 'questionario_compilato' | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Traduzioni (stessa regola di `functions/src/shared/i18n/testoMultilingua.ts`)
// ─────────────────────────────────────────────────────────────────────────────

/** I testi di una domanda in un'altra lingua. Ogni campo è facoltativo: manca = si legge la sorgente. */
export interface TraduzioneDomanda {
  testo?: string;
  nome_breve?: string;
  aiuto?: string;
  segnaposto?: string;
  /** Per id dell'opzione: id → etichetta tradotta. */
  opzioni?: { [opzioneId: string]: string | undefined };
  campo_dettaglio?: { etichetta?: string; segnaposto?: string };
  /** Per valore del tasto (in testo): «1» → «Just me». */
  scorciatoie?: { [valore: string]: string | undefined };
}

export interface TraduzioneQuestionario {
  titolo?: string;
  descrizione?: string;
  apertura?: Partial<SchermataApertura>;
  /** Per id della domanda. */
  domande?: { [domandaId: string]: TraduzioneDomanda | undefined };
  contatti?: {
    indicatore?: string;
    domanda?: string;
    aiuto?: string;
    bottone?: string;
    /** Per id del campo. */
    campi?: { [campoId: string]: { etichetta?: string; segnaposto?: string; nota?: string } | undefined };
    consenso?: { testo?: string; etichetta_informativa?: string; url_informativa?: string };
  };
  conferma?: {
    in_grande?: string;
    titolo?: string;
    testo?: string;
    testo_se_consenso?: string;
    collegamento?: Partial<CollegamentoConferma>;
  };
  email_conferma?: Partial<Omit<EmailConferma, 'attiva'>>;
}

/** Il questionario normalizzato: opzioni come oggetti, contatti sempre espliciti (o null). */
export interface Questionario extends Omit<QuestionarioSalvato, 'domande' | 'contatti'> {
  domande: Domanda[];
  contatti: SchermataContatti | null;
}

/** Valore normalizzato di una risposta. Le scelte salvano gli ID delle opzioni. */
export type ValoreRisposta = string | number | string[];

export interface RispostaSalvata {
  domanda_id: string;
  valore: ValoreRisposta;
  /** Il testo del campo di dettaglio, se compilato. */
  dettaglio?: string;
}

/** Da dove arriva chi compila: `?fonte=` e i parametri utm dell'indirizzo, più il sito di provenienza. */
export interface Provenienza {
  fonte: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  /** Solo il dominio (linkedin.com), mai l'indirizzo completo. */
  referrer_dominio: string | null;
  /** La lingua della pagina in cui si è compilato. */
  lingua_pagina: string | null;
}

/** I dati dell'ultima schermata, già puliti. */
export type ContattiRisposta = Partial<Record<CampoContattoId, string | null>>;

/** Lo stato di una risposta: una persona conta UNA volta (la sua risposta più recente). */
export type StatoRisposta = 'attuale' | 'sostituita' | 'ritirata';

/** La copia delle domande come le ha viste chi ha risposto: le statistiche restano giuste dopo una modifica. */
export interface IstantaneaDomanda {
  id: string;
  tipo: TipoDomanda;
  testo: string;
  nome_breve?: string;
  opzioni?: OpzioneDomanda[];
}

/** Il consenso agli aggiornamenti, come l'ha chiesto la persona in quella risposta. */
export interface ConsensoRichiesto {
  /** Il testo ESATTO che la persona ha visto, nella sua lingua. */
  testo: string;
  versione: string;
  canali: CanaleAggiornamenti[];
  /** La lingua di quel testo. */
  lingua: string;
}

/** Lo stato del consenso agli aggiornamenti sulla scheda del lead (doppia conferma via email). */
export type StatoConsensoAggiornamenti = 'da_confermare' | 'confermato' | 'revocato';

export interface VoceStoricoConsenso {
  stato: StatoConsensoAggiornamenti;
  /** ISO 8601. */
  il: string;
  come: 'modulo' | 'email_conferma' | 'email_uscita' | 'console';
  versione?: string;
  response_id?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Contratti delle funzioni pubbliche (richieste e risposte)
// ─────────────────────────────────────────────────────────────────────────────

/** getQuestionnaireForToken — richiesta. */
export interface RichiestaQuestionarioPubblico {
  token: string;
  /** La lingua della pagina: i testi arrivano tradotti in quella, dove la traduzione c'è. */
  lingua?: string;
  provenienza?: Partial<Provenienza>;
}

/** Ciò che la pagina pubblica riceve: niente email di conferma, niente dati interni. */
export interface QuestionarioPubblico {
  titolo: string;
  descrizione: string;
  /** La lingua richiesta, normalizzata; i testi senza traduzione restano nella sorgente. */
  lingua: string;
  domande: Domanda[];
  apertura: SchermataApertura | null;
  contatti: SchermataContatti | null;
  conferma: SchermataConferma | null;
  /** True se il link è personale: i contatti sono già noti e non si chiedono. */
  link_personale: boolean;
  /** Il biglietto della visita: torna con l'invio. */
  sessione_id: string;
}

/** submitQuestionnaireResponse — richiesta. */
export interface RichiestaInvioQuestionario {
  token: string;
  sessione_id: string;
  /** La lingua in cui la persona ha compilato: quella dei testi del consenso salvati e dell'email. */
  lingua?: string;
  risposte: Array<{ domanda_id: string; valore: unknown; dettaglio?: unknown }>;
  contatti?: Partial<Record<CampoContattoId, unknown>>;
  /** True solo se la persona ha spuntato la casella del consenso. */
  consenso_aggiornamenti?: boolean;
  durata_secondi?: number;
  /** Campo trappola: invisibile alle persone, lo compilano solo i programmi. */
  sito_web?: string;
  /** Il vecchio formato dei dati (ragione sociale + partita IVA), per i questionari senza `contatti`. */
  anagrafica?: Record<string, unknown>;
}

export interface EsitoInvioQuestionario {
  success: true;
  response_id: string;
}

/** azioneIscrizioneQuestionario — le tre cose che si fanno dai collegamenti dell'email. */
export const AZIONI_ISCRIZIONE = ['leggi', 'conferma_consenso', 'esci'] as const;
export type AzioneIscrizione = (typeof AZIONI_ISCRIZIONE)[number];

export interface RichiestaAzioneIscrizione {
  token: string;
  azione: AzioneIscrizione;
}

/** Ciò che la pagina del collegamento mostra: l'email mascherata, mai i dati interi. */
export interface StatoIscrizione {
  email_mascherata: string;
  titolo_questionario: string;
  consenso: StatoConsensoAggiornamenti | 'nessuno';
  /** True se la persona ha già chiesto di non essere più contattata. */
  uscito: boolean;
  /** True se il tempo per confermare il consenso è passato. */
  conferma_scaduta: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Limiti (tecnici, non di business)
// ─────────────────────────────────────────────────────────────────────────────

export const LIMITI_QUESTIONARI = {
  /** Risposte accettate in un invio. */
  max_risposte: 200,
  /** Lunghezza massima di un testo libero. */
  max_testo: 5000,
  /** Lunghezza massima di un campo dell'ultima schermata. */
  max_campo_contatto: 300,
  max_email: 320,
  /** Selezioni in una scelta multipla (tetto tecnico; quello vero è `massimo_scelte`). */
  max_selezioni: 100,
  /** Un intero più grande non ha senso in un questionario. */
  max_numero: 1_000_000,
  /** Secondi minimi fra l'apertura della pagina e l'invio: una persona non è così veloce. */
  secondi_minimi_compilazione: 5,
  /** Una visita vale un giorno. */
  ore_validita_sessione: 24,
  /** Aperture della pagina per indirizzo di rete in un'ora. */
  aperture_ora_per_ip: 60,
  /** Invii per indirizzo di rete in un'ora. */
  invii_ora_per_ip: 10,
  /** Giorni per confermare il consenso dall'email. */
  giorni_conferma_consenso: 30,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Le pagine pubbliche del motore (sul sito yumaos.com) — un posto solo per server e sito
// ─────────────────────────────────────────────────────────────────────────────

/** Dove vivono le pagine pubbliche. L'email di conferma costruisce i collegamenti da qui. */
export const ORIGINE_SITO_PUBBLICO = 'https://yumaos.com';

/**
 * Gli indirizzi delle pagine del motore, per lingua del sito. Il biglietto viaggia sempre come
 * parametro `t` (`?t=…`), così le pagine restano statiche e prerenderizzate.
 * - `questionario`: qualunque questionario, dal suo link (`?t=<token del link>`);
 * - `conferma`: la conferma del consenso agli aggiornamenti, dall'email;
 * - `uscita`: «non scrivetemi più», dall'email.
 * Una lingua senza indirizzi qui usa quelli della lingua base del sito ('it').
 */
export const PAGINE_PUBBLICHE_QUESTIONARI = {
  questionario: { it: 'questionario', en: 'questionnaire' },
  conferma: { it: 'iscrizione/conferma', en: 'subscription/confirm' },
  uscita: { it: 'iscrizione/esci', en: 'subscription/leave' },
} as const;
export type PaginaPubblicaQuestionari = keyof typeof PAGINE_PUBBLICHE_QUESTIONARI;
export type LinguaSitoPubblico = keyof (typeof PAGINE_PUBBLICHE_QUESTIONARI)['questionario'];

/** L'indirizzo completo di una pagina del motore, col biglietto. */
export function indirizzoPaginaPubblica(pagina: PaginaPubblicaQuestionari, lingua: string, biglietto: string): string {
  const slug = PAGINE_PUBBLICHE_QUESTIONARI[pagina] as Record<string, string>;
  const l = slug[lingua] ? lingua : 'it';
  return `${ORIGINE_SITO_PUBBLICO}/${l}/${slug[l]}?t=${encodeURIComponent(biglietto)}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// I documenti salvati (T = il tipo delle date: Timestamp dell'Admin SDK o del client)
// ─────────────────────────────────────────────────────────────────────────────

/** I dati del vecchio flusso (ragione sociale + partita IVA), salvati così dalle risposte di prima. */
export interface AnagraficaStorica {
  ragione_sociale: string;
  partita_iva: string;
  email: string | null;
  telefono: string | null;
  citta: string | null;
  provincia: string | null;
}

/** platform_questionnaire_responses/{id}. Immutabile salvo `stato`/`sostituita_da` (li cambia il server). */
export interface RispostaQuestionarioDoc<T = unknown> {
  questionnaire_id: string;
  link_token: string;
  platform_lead_id: string;
  risposte: RispostaSalvata[];
  /** Solo le risposte di prima del 18/10/2026. */
  anagrafica?: AnagraficaStorica | null;
  contatti?: ContattiRisposta | null;
  email_normalizzata?: string | null;
  /** Assente nelle risposte di prima: vale «attuale». */
  stato?: StatoRisposta;
  sostituita_da?: string | null;
  sessione_id?: string | null;
  provenienza?: Provenienza | null;
  /** La lingua in cui si è compilato. */
  lingua?: string;
  /** Presente solo se la persona ha spuntato la casella: il testo esatto che ha visto. */
  consenso?: ConsensoRichiesto | null;
  istantanea_domande?: IstantaneaDomanda[];
  titolo_questionario?: string;
  completato_at: T;
  durata_secondi: number | null;
  sintesi_ai: null | Record<string, unknown>;
  created_at: T;
}

/** Il consenso agli aggiornamenti sulla scheda del lead. */
export interface ConsensoAggiornamentiLead<T = unknown> {
  stato: StatoConsensoAggiornamenti;
  testo: string;
  versione: string;
  lingua: string;
  canali: CanaleAggiornamenti[];
  questionnaire_id: string;
  response_id: string;
  richiesto_at: T;
  confermato_at: T | null;
  revocato_at: T | null;
  /** Ogni cambio, solo aggiunte: la prova di ogni stato. */
  storico: VoceStoricoConsenso[];
}

/** I campi che il motore aggiunge a platform_leads/{id} (gli altri sono quelli del CRM). */
export interface CampiLeadDaQuestionario<T = unknown> {
  /** La chiave per riconoscere chi torna senza partita IVA. */
  email_normalizzata?: string | null;
  referente_nome?: string | null;
  referente_ruolo?: string | null;
  /** La provenienza della PRIMA risposta (il primo contatto). */
  provenienza?: Provenienza | null;
  consenso_aggiornamenti?: ConsensoAggiornamentiLead<T> | null;
  /** «Non scrivetemi più» (dal collegamento dell'email o dalla console): vince su tutto. */
  non_contattare?: boolean;
  non_contattare_at?: T | null;
}

/** platform_questionnaire_sessioni/{id}: una visita della pagina pubblica. */
export interface SessioneQuestionarioDoc<T = unknown> {
  link_token: string;
  questionnaire_id: string;
  lingua: string;
  provenienza: Provenienza;
  // Niente indirizzo di rete, nemmeno come impronta: serve solo ai contatori anti-abuso, che hanno
  // la loro raccolta e scadono in due giorni.
  creata_at: T;
  scade_at: T;
  usata_at: T | null;
  response_id: string | null;
}

/** platform_questionnaire_azioni/{sha256 del biglietto}: i collegamenti dell'email. */
export interface AzioneQuestionarioDoc<T = unknown> {
  platform_lead_id: string;
  response_id: string;
  questionnaire_id: string;
  email_normalizzata: string;
  lingua: string;
  creata_at: T;
  /** Null se la persona non ha chiesto gli aggiornamenti (il collegamento serve solo a uscire). */
  consenso_scade_at: T | null;
  consenso_confermato_at: T | null;
  uscita_at: T | null;
}
